<script lang="ts">
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import { formatIST } from '$lib/utils/dates';
  import { toast } from '$lib/state/toast.svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { KeyRound, Lock, History, Trash2 } from 'lucide-svelte';
  import { adminUsersApi, adminVerticalsApi, adminLoginsApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import SearchBar from '$lib/components/ui/SearchBar.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import UserActivityModal from './UserActivityModal.svelte';
  import type { AdminUser } from '$lib/types';

  const queryClient = useQueryClient();

  let search = $state('');
  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let formError = $state('');
  let tempPassword = $state('');

  let resetPasswordUser = $state<AdminUser | null>(null);
  let resetPasswordTempPassword = $state('');
  let activityUser = $state<AdminUser | null>(null);

  // ponytail: avatar color has no backend field of its own — cycle a small
  // deterministic palette by list position, same trick used elsewhere.
  const AVATAR_PALETTE = [
    { bg: '#E3F0F1', fg: '#226A73' },
    { bg: '#ECF8CC', fg: '#5c7a00' },
    { bg: '#FBF0DC', fg: '#8a5c00' },
    { bg: '#E4EEEF', fg: '#5F9CA3' }
  ];
  function avatarStyle(i: number) {
    return AVATAR_PALETTE[i % AVATAR_PALETTE.length];
  }
  function initials(row: AdminUser): string {
    return (
      (row.name || row.email)
        .trim()
        .split(/\s+/)
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase() || '?'
    );
  }

  function emptyForm() {
    return {
      email: '',
      name: '',
      role: 'member' as 'admin' | 'member',
      issues_only: false,
      dispatch_only: false,
      intake_coordinator: false,
      email_access: true,
      vertical_id: undefined as number | undefined,
      verticals: [] as number[],
      issue_vertical_ids: [] as number[],
      lead_vertical_ids: [] as number[],
      support_tickets: false
    };
  }
  let form = $state(emptyForm());

  // Verticals the user in the form can be subscribed to lead emails for:
  // their primary vertical plus any additional grants above.
  function grantedVerticalIds(): number[] {
    const ids = new Set<number>();
    if (form.vertical_id != null) ids.add(form.vertical_id);
    for (const id of form.verticals) ids.add(id);
    return [...ids];
  }

  function toggleLeadVertical(id: number) {
    const idx = form.lead_vertical_ids.indexOf(id);
    if (idx >= 0) form.lead_vertical_ids.splice(idx, 1);
    else form.lead_vertical_ids.push(id);
  }

  const usersQuery = createQuery(() => ({
    queryKey: ['admin-users', { search }],
    queryFn: () => adminUsersApi.list({ q: search || undefined, per_page: 100 })
  }));

  const verticalsQuery = createQuery(() => ({
    queryKey: ['admin-verticals'],
    queryFn: () => adminVerticalsApi.list()
  }));

  function verticalNames(row: AdminUser): string {
    const names = (verticalsQuery.data?.results ?? [])
      .filter((v) => row.verticals.includes(v.id))
      .map((v) => v.name);
    return names.length ? names.join(', ') : '—';
  }

  function formatWhen(iso: string | null): string {
    if (!iso) return 'Never';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  }

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? adminUsersApi.update(editingId, body) : adminUsersApi.create(body),
    onSuccess: (data: AdminUser) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      if (data.temp_password) {
        tempPassword = data.temp_password;
      } else {
        closeForm();
      }
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => adminUsersApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  }));

  const resetTotpMutation = createMutation(() => ({
    mutationFn: (id: number) => adminUsersApi.resetTotp(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  }));

  const resetPasswordMutation = createMutation(() => ({
    mutationFn: (id: number) => adminUsersApi.resetPassword(id),
    onSuccess: (data: { temp_password: string }) => {
      resetPasswordTempPassword = data.temp_password;
    },
    onError: (err: unknown) => {
      resetPasswordUser = null;
      toast.error(getErrorMessage(err));
    }
  }));

  function openCreate() {
    editingId = null;
    form = emptyForm();
    formError = '';
    tempPassword = '';
    showForm = true;
  }

  function openEdit(row: AdminUser) {
    editingId = row.id;
    form = {
      email: row.email,
      name: row.name,
      role: row.role,
      issues_only: row.issues_only,
      dispatch_only: row.dispatch_only,
      intake_coordinator: !!row.intake_coordinator,
      email_access: row.email_access,
      vertical_id: row.vertical_id ?? undefined,
      verticals: [...row.verticals],
      issue_vertical_ids: [...(row.issue_vertical_ids ?? [])],
      lead_vertical_ids: [...row.lead_vertical_ids],
      support_tickets: row.support_tickets
    };
    formError = '';
    tempPassword = '';
    showForm = true;
  }

  // The table's "Access" switch toggles issues_only directly (matching the
  // prototype), independent of the full edit form.
  const toggleAccessMutation = createMutation(() => ({
    mutationFn: (row: AdminUser) => adminUsersApi.update(row.id, { issues_only: !row.issues_only }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  }));

  // Same switch pattern as toggleAccessMutation, for the "email access" flag
  // (see internal/models/user.go's EmailAccess — gates mailbox connect/sync
  // and email touchpoint visibility).
  const toggleEmailAccessMutation = createMutation(() => ({
    mutationFn: (row: AdminUser) => adminUsersApi.update(row.id, { email_access: !row.email_access }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  }));

  const loginsQuery = createQuery(() => ({
    queryKey: ['admin-logins-recent'],
    queryFn: () => adminLoginsApi.recent({ limit: 100 })
  }));

  function closeForm() {
    showForm = false;
    tempPassword = '';
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!editingId && !form.email.trim()) {
      formError = 'Email is required';
      return;
    }
    formError = '';
    // Drop any lead subscription for a vertical no longer granted above
    // (e.g. its "additional verticals" checkbox was unticked after the
    // subscription checkbox was ticked) — the backend rejects those anyway.
    const granted = grantedVerticalIds();
    form.lead_vertical_ids = form.lead_vertical_ids.filter((id) => granted.includes(id));
    const body: Record<string, unknown> = { ...form };
    if (editingId) delete body.email; // email is immutable after creation
    saveMutation.mutate(body);
  }

  function toggleVertical(id: number) {
    const idx = form.verticals.indexOf(id);
    if (idx >= 0) form.verticals.splice(idx, 1);
    else form.verticals.push(id);
  }

  function toggleIssueVertical(id: number) {
    const idx = form.issue_vertical_ids.indexOf(id);
    if (idx >= 0) form.issue_vertical_ids.splice(idx, 1);
    else form.issue_vertical_ids.push(id);
  }

  async function remove(row: AdminUser) {
    if (await confirmAction(`Delete user "${row.email}"? This cannot be undone.`)) deleteMutation.mutate(row.id);
  }

  async function resetTotp(row: AdminUser) {
    if (await confirmAction(`Reset two-factor for "${row.email}"? They will re-enroll at next login.`)) {
      resetTotpMutation.mutate(row.id);
    }
  }

  async function resetPassword(row: AdminUser) {
    if (
      await confirmAction(
        `Reset password for "${row.email}"? Their existing sessions will be signed out immediately.`
      )
    ) {
      resetPasswordUser = row;
      resetPasswordTempPassword = '';
      resetPasswordMutation.mutate(row.id);
    }
  }

  function closeResetPassword() {
    resetPasswordUser = null;
    resetPasswordTempPassword = '';
  }

  function openActivity(row: AdminUser) {
    activityUser = row;
  }

  function closeActivity() {
    activityUser = null;
  }
</script>

<div>
  <div class="mb-[18px] flex items-center">
    <div>
      <h2 class="tym-section-title">Users &amp; access</h2>
      <div class="tym-section-sub">
        {usersQuery.data?.count ?? 0} staff accounts &middot; role, vertical &amp; access grants
      </div>
    </div>
  </div>

  <SearchBar bind:value={search} onCreate={openCreate} createLabel="Add user" />

  {#if usersQuery.isLoading}
    <p class="tym-loading-state">Loading users&hellip;</p>
  {:else if usersQuery.isError}
    <p class="tym-error-state">{getErrorMessage(usersQuery.error)}</p>
  {:else if usersQuery.data}
    <div class="tym-table-wrap">
      <div class="tym-table-scroll">
        <table class="tym-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Verticals</th>
              <th>2FA</th>
              <th>Access</th>
              <th>Last active</th>
              <th class="num">Actions</th>
            </tr>
          </thead>
          <tbody>
            {#each usersQuery.data.results as row, i (row.id)}
              {@const av = avatarStyle(i)}
              <tr class="clickable" onclick={() => openEdit(row)}>
                <td>
                  <div class="flex items-center gap-[11px]">
                    <span
                      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-heading text-xs font-bold"
                      style="background:{av.bg}; color:{av.fg}"
                    >
                      {initials(row)}
                    </span>
                    <div>
                      <div class="text-[13.5px] font-semibold text-navy">{row.name || row.email}</div>
                      <div class="text-[11.5px] text-dark-gray">{row.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span
                    class="tym-badge capitalize"
                    style={row.role === 'admin' ? 'background:#E6E9EE; color:#10223A' : 'background:#F1F0EF; color:#666'}
                    >{row.role}</span
                  >
                </td>
                <td class="muted">{verticalNames(row)}</td>
                <td class="muted">{row.totp_enabled ? 'Enrolled' : 'Not enrolled'}</td>
                <td>
                  <div class="flex flex-col items-start gap-1.5">
                    {#if row.intake_coordinator}<span class="tym-badge">Intake coordinator</span>{/if}
                    {#if row.dispatch_only}
                      <span class="tym-badge">Dispatch only</span>
                    {:else}
                    <button
                      type="button"
                      title="Restrict this user to the Issues module only"
                      class="inline-flex items-center gap-2 border-0 bg-transparent p-0"
                      onclick={(e) => {
                        e.stopPropagation();
                        toggleAccessMutation.mutate(row);
                      }}
                    >
                      <span
                        class="relative inline-block h-5 w-[34px] rounded-pill transition-colors"
                        style="background:{row.issues_only ? '#97D700' : '#DBDBDB'}"
                      >
                        <span
                          class="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-[0_1px_3px_rgba(16,34,58,.3)] transition-[left]"
                          style="left:{row.issues_only ? '16px' : '2px'}"
                        ></span>
                      </span>
                      <span class="text-[11.5px] font-semibold" style="color:{row.issues_only ? '#10223A' : '#999D9E'}"
                        >Issues only</span
                      >
                    </button>
                    <button
                      type="button"
                      title="Allow connecting a mailbox and seeing synced email touchpoints"
                      class="inline-flex items-center gap-2 border-0 bg-transparent p-0"
                      onclick={(e) => {
                        e.stopPropagation();
                        toggleEmailAccessMutation.mutate(row);
                      }}
                    >
                      <span
                        class="relative inline-block h-5 w-[34px] rounded-pill transition-colors"
                        style="background:{row.email_access ? '#97D700' : '#DBDBDB'}"
                      >
                        <span
                          class="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-[0_1px_3px_rgba(16,34,58,.3)] transition-[left]"
                          style="left:{row.email_access ? '16px' : '2px'}"
                        ></span>
                      </span>
                      <span class="text-[11.5px] font-semibold" style="color:{row.email_access ? '#10223A' : '#999D9E'}"
                        >Email access</span
                      >
                    </button>
                    {/if}
                  </div>
                </td>
                <td class="muted">{formatWhen(row.last_active)}</td>
                <td class="num">
                  <div class="flex justify-end gap-1.5">
                    <button
                      type="button"
                      class="btn-secondary !h-10 !w-10 !p-0"
                      onclick={(e) => {
                        e.stopPropagation();
                        openActivity(row);
                      }}
                      aria-label="Login activity"
                      title="Login activity"
                    >
                      <History class="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      class="btn-secondary !h-10 !w-10 !p-0"
                      onclick={(e) => {
                        e.stopPropagation();
                        resetPassword(row);
                      }}
                      aria-label="Reset password"
                      title="Reset password"
                    >
                      <Lock class="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      class="btn-secondary !h-10 !w-10 !p-0"
                      onclick={(e) => {
                        e.stopPropagation();
                        resetTotp(row);
                      }}
                      aria-label="Reset two-factor"
                      title="Reset two-factor"
                    >
                      <KeyRound class="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      class="btn-danger !h-10 !w-10 !p-0"
                      onclick={(e) => {
                        e.stopPropagation();
                        remove(row);
                      }}
                      aria-label="Delete"
                      title="Delete"
                    >
                      <Trash2 class="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            {:else}
              <tr><td class="empty" colspan="7">No users found.</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <h3 class="mb-1 mt-6 font-heading text-[15px] font-bold text-navy">Login activity</h3>
    <div class="tym-section-sub mb-3">Global feed &middot; time, IP, device</div>
    {#if loginsQuery.isLoading}
      <p class="tym-loading-state">Loading logins&hellip;</p>
    {:else if loginsQuery.isError}
      <p class="tym-error-state">{getErrorMessage(loginsQuery.error)}</p>
    {:else if loginsQuery.data}
      <div class="tym-table-wrap">
        <div class="tym-table-scroll">
          <table class="tym-table">
            <thead>
              <tr>
                <th>User</th>
                <th>When</th>
                <th>IP address</th>
                <th>Device</th>
              </tr>
            </thead>
            <tbody>
              {#each loginsQuery.data.results as row (row.id)}
                <tr>
                  <td class="font-medium text-navy">{row.email ?? `#${row.user_id}`}</td>
                  <td class="muted">{formatIST(row.created_at)}</td>
                  <td class="muted font-heading">{row.ip_address || '—'}</td>
                  <td>
                    <span class="tym-badge" style="background:#F1F0EF; color:#666">
                      {row.browser} &middot; {row.os} &middot; {row.device}
                    </span>
                  </td>
                </tr>
              {:else}
                <tr><td class="empty" colspan="4">No logins recorded yet.</td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {/if}
  {/if}
</div>

{#if showForm}
  <Modal title={editingId ? 'Edit user' : 'New user'} onClose={closeForm}>
    {#if tempPassword}
      <div class="space-y-4">
        <div class="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          Share this temporary password with the user. It won't be shown again.
        </div>
        <p class="font-mono text-lg text-center bg-gray-50 rounded-md py-3">{tempPassword}</p>
        <button type="button" class="btn-primary w-full" onclick={closeForm}>Done</button>
      </div>
    {:else}
      <form class="space-y-4" onsubmit={submit}>
        <div>
          <label class="label" for="email">Email</label>
          <input id="email" type="email" class="input" bind:value={form.email} disabled={!!editingId} />
        </div>
        <div>
          <label class="label" for="name">Name</label>
          <input id="name" class="input" bind:value={form.name} />
        </div>
        <div>
          <label class="label" for="role">Role</label>
          <select id="role" class="input" bind:value={form.role}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <label class="flex items-center gap-2.5 rounded-lg border border-toyama-cloudy-gray px-3 py-2.5 text-[13px] text-navy">
          <input type="checkbox" bind:checked={form.issues_only} />
          <span class="flex-1">
            <span class="block font-semibold">Issues only</span>
            <span class="block text-[11.5px] font-normal text-dark-gray"
              >Restricts this account to the Issues module only (field-technician).</span
            >
          </span>
        </label>
        <label class="flex items-center gap-2.5 rounded-lg border border-toyama-cloudy-gray px-3 py-2.5 text-[13px] text-navy">
          <input type="checkbox" bind:checked={form.dispatch_only} />
          <span><strong>Dispatch only</strong><br />Member accounts only. Grant Dastageer, Khudoos and/or Ahad. No pricing, CRM modules, email or product editing.</span>
        </label>
        <label class="flex items-center gap-2.5 rounded-lg border border-toyama-cloudy-gray px-3 py-2.5 text-[13px] text-navy">
          <input type="checkbox" bind:checked={form.intake_coordinator} />
          <span><strong>Intake coordinator</strong><br />Allows assigning and transferring contacts within granted full-CRM pipelines. Handles enquiry intake, salesperson meetings and satisfaction callbacks; does not grant admin access or make this user the sales owner after handover. Grant the required full-CRM verticals below; issue-only grants do not grant contact access.</span>
        </label>
        <label class="flex items-center gap-2.5 rounded-lg border border-toyama-cloudy-gray px-3 py-2.5 text-[13px] text-navy">
          <input type="checkbox" bind:checked={form.email_access} />
          <span class="flex-1">
            <span class="block font-semibold">Email access</span>
            <span class="block text-[11.5px] font-normal text-dark-gray"
              >Allows connecting a mailbox and seeing synced email touchpoints.</span
            >
          </span>
        </label>
        <div>
          <label class="label" for="vertical_id">Primary vertical</label>
          <select id="vertical_id" class="input" bind:value={form.vertical_id}>
            <option value={undefined}>-- none --</option>
            {#each verticalsQuery.data?.results ?? [] as v (v.id)}
              <option value={v.id}>{v.name}</option>
            {/each}
          </select>
        </div>
        <div>
          <span class="label">Additional verticals</span>
          <div class="flex flex-wrap gap-2">
            {#each verticalsQuery.data?.results ?? [] as v (v.id)}
              <button
                type="button"
                class="tym-chip !min-h-0 !px-2.5 !py-1 !text-xs {form.verticals.includes(v.id) ? 'active' : ''}"
                onclick={() => toggleVertical(v.id)}
              >
                {v.name}
              </button>
            {/each}
          </div>
        </div>

        <div>
          <span class="label">Customer-care teams (issues only)</span>
          <p class="mb-2 text-[12px] text-dark-gray">Adds issue access for these teams, without sales, people, or leads access. Primary and additional verticals above retain their full CRM permissions.</p>
          <div class="space-y-1.5">
            {#each verticalsQuery.data?.results ?? [] as v (v.id)}
              <label class="flex items-center gap-2 text-[13px] text-navy">
                <input type="checkbox" checked={form.issue_vertical_ids.includes(v.id)} onchange={() => toggleIssueVertical(v.id)} />
                {v.name}
              </label>
            {/each}
          </div>
        </div>

        <div>
          <span class="label">Notifications</span>
          <div class="space-y-1.5">
            {#each verticalsQuery.data?.results.filter((v) => grantedVerticalIds().includes(v.id)) ?? [] as v (v.id)}
              <label class="flex items-center gap-2 text-[13px] text-navy">
                <input
                  type="checkbox"
                  checked={form.lead_vertical_ids.includes(v.id)}
                  onchange={() => toggleLeadVertical(v.id)}
                />
                {v.name} lead emails
              </label>
            {:else}
              <p class="text-[12px] text-dark-gray">Grant a vertical above to enable its lead emails.</p>
            {/each}
            <label class="flex items-center gap-2 text-[13px] text-navy">
              <input type="checkbox" bind:checked={form.support_tickets} />
              Support ticket emails
            </label>
          </div>
        </div>

        {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}

        <div class="flex justify-end gap-2 pt-2">
          <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
          <button type="submit" class="btn-primary" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    {/if}
  </Modal>
{/if}

{#if resetPasswordUser}
  <Modal title="Reset password" onClose={closeResetPassword}>
    {#if resetPasswordTempPassword}
      <div class="space-y-4">
        <div class="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          Share this temporary password with {resetPasswordUser.email}. It won't be shown again.
          Their existing sessions have been signed out.
        </div>
        <p class="font-mono text-lg text-center bg-gray-50 rounded-md py-3">{resetPasswordTempPassword}</p>
        <button type="button" class="btn-primary w-full" onclick={closeResetPassword}>Done</button>
      </div>
    {:else}
      <p class="text-sm text-gray-500">Resetting password...</p>
    {/if}
  </Modal>
{/if}

{#if activityUser}
  <UserActivityModal user={activityUser} onClose={closeActivity} />
{/if}
